'use client';

import React, { useState, useEffect, useRef } from 'react';

const FLOW_STEPS = [
  {
    id: 'question',
    label: 'Question',
    icon: '?',
    desc: 'You ask in plain language — any language, any format.',
    detail: '"Which products should I restock before weekend demand?"',
    color: '#1F4D3F',
  },
  {
    id: 'context',
    label: 'Business Context',
    icon: '◎',
    desc: 'VINA maps your question to your specific business structure.',
    detail: '3 outlets · 4,012 SKUs · Storeex 247 · Ahmedabad, Surat, Vadodara',
    color: '#2D6A5A',
  },
  {
    id: 'data',
    label: 'Live Data',
    icon: '⟳',
    desc: 'Real-time data pulled from connected sources.',
    detail: 'Inventory · Sales · Purchase · POS · CRM — all live',
    color: '#3A7D6B',
  },
  {
    id: 'analysis',
    label: 'Analysis',
    icon: '≋',
    desc: 'Patterns, anomalies, and trends identified instantly.',
    detail: '7 SKUs below reorder · Britannia Bourbon at 142 vs 500 threshold',
    color: '#2D6A5A',
  },
  {
    id: 'recommendation',
    label: 'Recommendation',
    icon: '→',
    desc: 'A clear, actionable recommendation surfaces.',
    detail: 'Draft PO to Sunrise Distributors · ₹4,28,950 · Net 30',
    color: '#1F4D3F',
  },
  {
    id: 'action',
    label: 'Action',
    icon: '✓',
    desc: 'One click to execute — or approve and move on.',
    detail: 'Purchase order sent for approval · Audit log updated',
    color: '#16352D',
  },
];

export default function HowVinaThinks() {
  const [activeStep, setActiveStep] = useState(0);
  const [mounted, setMounted] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!mounted) return;
    intervalRef.current = setInterval(() => {
      setActiveStep(s => (s + 1) % FLOW_STEPS.length);
    }, 2000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [mounted]);

  const handleStepClick = (i: number) => {
    setActiveStep(i);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveStep(s => (s + 1) % FLOW_STEPS.length);
    }, 2000);
  };

  return (
    <section
      id="how-vina-thinks"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #FBFAF6 0%, #F6F5F0 100%)' }}
      aria-label="How VINA thinks"
    >
      {/* Ambient particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {mounted && [
          { x: '15%', y: '20%', size: 3, delay: '0s', dur: '4s' },
          { x: '80%', y: '15%', size: 2, delay: '1s', dur: '5s' },
          { x: '60%', y: '70%', size: 4, delay: '0.5s', dur: '6s' },
          { x: '25%', y: '80%', size: 2, delay: '2s', dur: '4.5s' },
          { x: '90%', y: '50%', size: 3, delay: '1.5s', dur: '5.5s' },
          { x: '45%', y: '30%', size: 2, delay: '0.8s', dur: '4s' },
        ].map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              left: p.x,
              top: p.y,
              width: p.size,
              height: p.size,
              background: 'rgba(31,77,63,0.3)',
              animation: `particleFloat ${p.dur} ease-in-out ${p.delay} infinite alternate`,
            }}
          />
        ))}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] opacity-10"
          style={{ background: 'radial-gradient(ellipse, rgba(31,77,63,0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.08em', color: '#7B8F88', textTransform: 'uppercase', marginBottom: '16px' }}>
            Section 05
          </p>
          <h2 style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em', color: '#0D2620', lineHeight: 1.1, marginBottom: '16px' }}>
            How VINA thinks.
          </h2>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: '1.1rem', color: '#3D5550', maxWidth: '480px', margin: '0 auto', lineHeight: 1.65 }}>
            From question to action — a decision engine that understands your business.
          </p>
        </div>

        {/* Flow visualization */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — Step list */}
          <div className="flex flex-col gap-2">
            {FLOW_STEPS.map((step, i) => {
              const isActive = activeStep === i;
              const isPast = i < activeStep;
              return (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(i)}
                  className="w-full text-left flex items-start gap-4 px-5 py-4 rounded-2xl transition-all"
                  style={{
                    background: isActive ? 'rgba(255,255,255,0.95)' : 'transparent',
                    border: isActive ? '1px solid rgba(31,77,63,0.2)' : '1px solid transparent',
                    boxShadow: isActive ? '0 8px 32px rgba(31,77,63,0.08)' : 'none',
                    transition: 'all 0.35s cubic-bezier(0.22,1,0.36,1)',
                  }}
                  aria-label={`Step ${i + 1}: ${step.label}`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {/* Step indicator */}
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all"
                    style={{
                      background: isActive ? step.color : isPast ? 'rgba(31,77,63,0.12)' : 'rgba(211,223,219,0.5)',
                      color: isActive ? '#FFFFFF' : isPast ? '#1F4D3F' : '#9AADA7',
                      transition: 'all 0.35s ease',
                    }}
                  >
                    {isPast ? (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px' }}>{String(i + 1).padStart(2, '0')}</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p style={{
                      fontFamily: 'var(--font-sans)',
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '14px',
                      color: isActive ? '#0D2620' : '#4F665F',
                      marginBottom: isActive ? '4px' : '0',
                      transition: 'all 0.3s ease',
                    }}>
                      {step.label}
                    </p>
                    {isActive && (
                      <p style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: '13px',
                        color: '#3D5550',
                        lineHeight: 1.5,
                        animation: 'fadeSlideUp 0.3s ease both',
                      }}>
                        {step.desc}
                      </p>
                    )}
                  </div>

                  {/* Active progress bar */}
                  {isActive && (
                    <div className="flex-shrink-0 w-1 self-stretch rounded-full overflow-hidden"
                      style={{ background: 'rgba(31,77,63,0.1)' }}>
                      <div className="w-full rounded-full"
                        style={{
                          background: step.color,
                          height: '100%',
                          animation: 'progressFill 2s linear both',
                        }} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right — Detail card */}
          <div>
            {(() => {
              const step = FLOW_STEPS[activeStep];
              return (
                <div
                  key={activeStep}
                  className="rounded-3xl p-8"
                  style={{
                    background: 'rgba(255,255,255,0.88)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(211,223,219,0.8)',
                    boxShadow: '0 24px 60px rgba(31,77,63,0.1)',
                    animation: 'fadeSlideUp 0.4s cubic-bezier(0.22,1,0.36,1) both',
                  }}
                >
                  {/* Step number */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl"
                      style={{ background: `${step.color}15`, color: step.color }}>
                      {step.icon}
                    </div>
                    <div>
                      <p style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#7B8F88', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Step {activeStep + 1} of {FLOW_STEPS.length}
                      </p>
                      <p style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: '20px', color: '#0D2620', letterSpacing: '-0.02em' }}>
                        {step.label}
                      </p>
                    </div>
                  </div>

                  <div className="h-px mb-6" style={{ background: 'rgba(211,223,219,0.6)' }} />

                  <p style={{ fontFamily: 'var(--font-sans)', fontSize: '15px', color: '#1A3530', lineHeight: 1.7, marginBottom: '20px' }}>
                    {step.desc}
                  </p>

                  <div className="px-4 py-3 rounded-2xl"
                    style={{ background: `${step.color}08`, border: `1px solid ${step.color}18` }}>
                    <p style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: step.color, letterSpacing: '0.02em', lineHeight: 1.6 }}>
                      {step.detail}
                    </p>
                  </div>

                  {/* Progress dots */}
                  <div className="flex gap-1.5 mt-6">
                    {FLOW_STEPS.map((_, i) => (
                      <div key={i} className="rounded-full transition-all duration-400"
                        style={{
                          width: i === activeStep ? '20px' : '6px',
                          height: '6px',
                          background: i === activeStep ? step.color : i < activeStep ? `${step.color}40` : 'rgba(211,223,219,0.8)',
                        }} />
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes progressFill {
          from { transform: scaleY(0); transform-origin: top; }
          to { transform: scaleY(1); transform-origin: top; }
        }
        @keyframes particleFloat {
          from { transform: translateY(0px) scale(1); opacity: 0.3; }
          to { transform: translateY(-20px) scale(1.2); opacity: 0.6; }
        }
      `}</style>
    </section>
  );
}
